/**
 * Docs Library - Internal documentation with Notion-style viewer
 *
 * Clean markdown rendering with sidebar navigation
 */

import { useState } from 'react'
import {
  Book,
  ChevronRight,
  ChevronDown,
  Search,
  Star,
  Clock,
} from 'lucide-react'
import { cn } from '../lib/utils'

// Sample documentation content - declared first
const WELCOME_DOC = `
# Welcome to LUKHAS

Welcome to the team! This guide will help you get started with our internal systems and tools.

## What is LUKHAS?

LUKHAS (Logic Unified Knowledge Hyper Adaptable System) is a consciousness-aware AI development platform. We're building the future of human-AI collaboration.

## Your First Week

- Complete your onboarding checklist
- Set up your development environment
- Meet with your team lead
- Review the architecture documentation

## Key Resources

- Internal Slack channels
- GitHub organization
- Notion workspace
- This documentation portal

> "The best way to predict the future is to create it." - Alan Kay
`

const QUICKSTART_DOC = `
# Quick Start Guide

Get up and running with LUKHAS development in minutes.

## Prerequisites

- Python 3.11+
- Node.js 18+
- Git
- VS Code (recommended)

## Setup Steps

### 1. Clone the Repository

Clone the main LUKHAS repository to your local machine.

### 2. Install Dependencies

Run the bootstrap command to install all dependencies.

### 3. Configure Environment

Copy the example environment file and update with your credentials.

### 4. Start Development

Run the development server and start building.

## Next Steps

- Read the Architecture documentation
- Review the Coding Standards
- Set up your IDE extensions
`

const ARCHITECTURE_DOC = `
# System Architecture

Overview of the LUKHAS platform architecture.

## Lane-Based Development

LUKHAS uses a three-lane architecture for safe AI development:

### Development Lane (candidate/)

- Experimental consciousness research
- Prototyping new features
- 2,877 files

### Integration Lane (core/)

- Testing and validation
- 253 components

### Production Lane (lukhas/)

- Battle-tested systems
- 692 components

## Constellation Framework

The 8-Star System coordinates foundational capabilities:

- Identity: Authentication and access
- Memory: State persistence
- Vision: Pattern recognition
- Bio: Organic adaptation
- Dream: Creative synthesis
- Ethics: Value alignment
- Guardian: Ethical enforcement
- Quantum: Quantum-inspired algorithms

## MATRIZ Engine

Core cognitive processing with:
- <250ms p95 latency
- <100MB memory
- 50+ ops/sec throughput
`

const CODING_DOC = `
# Coding Standards

Guidelines for writing clean, maintainable code.

## Python Style

- Follow PEP 8
- Use type hints
- Maximum line length: 88 characters
- Use Black for formatting

## TypeScript Style

- Use TypeScript strict mode
- Prefer interfaces over types
- Use ESLint and Prettier

## Commit Messages

Follow the T4 commit format:

- feat(scope): Add new feature
- fix(scope): Fix bug
- docs(scope): Update documentation

## Code Review

All code must be reviewed before merging:

- At least one approval required
- All tests must pass
- No linter errors
`

const GIT_DOC = `
# Git Workflow

Our branching and collaboration strategy.

## Branch Naming

- feat/feature-name
- fix/bug-description
- docs/documentation-update
- refactor/component-name

## Worktree Policy

All parallel work must use git worktrees:

- Create worktree for each task
- Never work directly on main
- Clean up after merge

## Pull Requests

- Create from feature branch
- Link related issues
- Add clear description
- Request appropriate reviewers
`

const TESTING_DOC = `
# Testing Guidelines

Comprehensive testing standards for LUKHAS.

## Test Types

### Unit Tests
Test individual components in isolation.

### Integration Tests
Test component interactions.

### E2E Tests
Test complete user workflows.

## Coverage Requirements

- Minimum 75% coverage for production code
- 100% coverage for critical paths
- All edge cases documented

## Running Tests

Use pytest for Python tests with appropriate markers:
- smoke: Quick health checks
- unit: Component tests
- integration: Cross-system tests
`

const IDENTITY_DOC = `
# Identity Star

Authentication and access management.

## Overview

The Identity Star handles all authentication, authorization, and user identity management within LUKHAS.

## Key Features

- Multi-factor authentication
- SSO integration
- Role-based access control
- API key management

## Architecture

Identity uses a layered approach:
- Authentication layer
- Authorization layer
- Session management
- Audit logging
`

const MEMORY_DOC = `
# Memory Star

Persistent state and context preservation.

## Overview

Memory Star handles state management across sessions and provides context preservation for AI interactions.

## Components

- Short-term memory
- Long-term storage
- Context windows
- Recall mechanisms

## Performance

- Sub-100ms retrieval
- Semantic search
- Temporal indexing
`

const VISION_DOC = `
# Vision Star

Perception and pattern recognition systems.

## Overview

Vision Star handles all visual processing, pattern recognition, and perceptual understanding within LUKHAS.

## Capabilities

- Image understanding
- Document analysis
- Visual pattern matching
- Multi-modal perception

## Architecture

Vision processing pipeline:
- Input preprocessing
- Feature extraction
- Pattern matching
- Semantic understanding
`

const BIO_DOC = `
# Bio Star

Bio-inspired adaptation and organic growth patterns.

## Overview

Bio Star implements nature-inspired algorithms and organic adaptation patterns for system resilience.

## Key Concepts

- Evolutionary algorithms
- Adaptive learning
- Self-healing systems
- Growth-based scaling

## Applications

- Load balancing adaptation
- Resource optimization
- Fault tolerance
- System evolution
`

const DREAM_DOC = `
# Dream Star

Creative synthesis and unconscious processing.

## Overview

Dream Star enables creative problem-solving through synthesis patterns inspired by human dreaming and imagination.

## Features

- Creative ideation
- Pattern synthesis
- Novel solution generation
- Lateral thinking support

## Integration

Dream processes run during:
- Low-priority background tasks
- Creative synthesis workflows
- Innovation sprints
`

const ETHICS_DOC = `
# Ethics Star

Moral reasoning and value alignment frameworks.

## Overview

Ethics Star provides the moral reasoning framework ensuring all LUKHAS operations align with human values.

## Core Principles

- Beneficence
- Non-maleficence
- Autonomy respect
- Fairness and justice

## Decision Framework

Ethical evaluation at:
- Input assessment
- Action planning
- Output validation
- Consequence analysis
`

const GUARDIAN_DOC = `
# Guardian Star

Constitutional AI and ethical enforcement.

## Purpose

Guardian ensures all AI operations align with ethical guidelines and constitutional principles.

## Capabilities

- Content filtering
- Bias detection
- Ethical scoring
- Drift monitoring

## Integration

Guardian integrates at multiple points:
- Input validation
- Output filtering
- Continuous monitoring
`

const QUANTUM_DOC = `
# Quantum Star

Quantum-inspired algorithms and processing.

## Overview

Quantum Star implements quantum-inspired algorithms for enhanced optimization and parallel processing.

## Techniques

- Superposition patterns
- Entanglement-inspired coordination
- Quantum annealing optimization
- Probabilistic computing

## Applications

- Complex optimization
- Multi-path exploration
- Enhanced search algorithms
- Parallel decision making
`

const MATRIZ_DOC = `
# MATRIZ Pipeline

Memory → Attention → Thought → Risk → Intent → Action

## Overview

MATRIZ is the core cognitive engine of LUKHAS, implementing a sequential processing pipeline inspired by human cognition.

## Pipeline Stages

### Memory (📜)
Retrieval and context loading from persistent storage.

### Attention (🔍)
Focus mechanisms and relevance filtering.

### Thought (💭)
Reasoning, analysis, and synthesis.

### Risk (⚖️)
Safety assessment and risk evaluation.

### Intent (🎯)
Goal alignment and intention formulation.

### Action (⚡)
Execution and output generation.

## Performance Targets

- < 250ms p95 latency
- < 100MB memory footprint
- 50+ ops/sec throughput
`

const SECURITY_DOC = `
# Security Policy

Security standards and practices.

## Authentication

- Use SSO when available
- Enable 2FA for all accounts
- Regular password rotation

## Data Handling

- No PII in logs
- Encrypt at rest and in transit
- Minimal data retention

## Incident Response

- Report immediately to security team
- Document all details
- Follow escalation procedures
`

const HR_DOC = `
# HR Handbook

Human resources policies and procedures.

## Time Off

- Unlimited PTO policy
- Minimum 2 weeks encouraged
- Advance notice required

## Remote Work

- Fully remote friendly
- Core hours: 10am-3pm local
- Monthly team sync

## Growth

- Annual reviews
- Learning budget
- Conference attendance
`

interface DocItem {
  id: string
  title: string
  type: 'folder' | 'doc'
  icon?: string
  children?: DocItem[]
  content?: string
  lastUpdated?: string
  starred?: boolean
}

const DOCS_TREE: DocItem[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    type: 'folder',
    icon: '🚀',
    children: [
      { id: 'welcome', title: 'Welcome to LUKHAS', type: 'doc', icon: '👋', lastUpdated: '2 days ago', content: WELCOME_DOC },
      { id: 'quick-start', title: 'Quick Start Guide', type: 'doc', icon: '⚡', lastUpdated: '1 week ago', content: QUICKSTART_DOC },
      { id: 'architecture', title: 'System Architecture', type: 'doc', icon: '🏗️', lastUpdated: '3 days ago', starred: true, content: ARCHITECTURE_DOC },
    ]
  },
  {
    id: 'development',
    title: 'Development',
    type: 'folder',
    icon: '💻',
    children: [
      { id: 'coding-standards', title: 'Coding Standards', type: 'doc', icon: '📝', lastUpdated: '5 days ago', content: CODING_DOC },
      { id: 'git-workflow', title: 'Git Workflow', type: 'doc', icon: '🌿', lastUpdated: '1 week ago', content: GIT_DOC },
      { id: 'testing', title: 'Testing Guidelines', type: 'doc', icon: '🧪', lastUpdated: '2 weeks ago', content: TESTING_DOC },
    ]
  },
  {
    id: 'constellation',
    title: 'Constellation Framework',
    type: 'folder',
    icon: '✨',
    children: [
      { id: 'identity', title: 'Identity Star', type: 'doc', icon: '⚛️', lastUpdated: '4 days ago', starred: true, content: IDENTITY_DOC },
      { id: 'memory', title: 'Memory Star', type: 'doc', icon: '✦', lastUpdated: '1 week ago', content: MEMORY_DOC },
      { id: 'vision', title: 'Vision Star', type: 'doc', icon: '🔬', lastUpdated: '5 days ago', content: VISION_DOC },
      { id: 'bio', title: 'Bio Star', type: 'doc', icon: '🌱', lastUpdated: '6 days ago', content: BIO_DOC },
      { id: 'dream', title: 'Dream Star', type: 'doc', icon: '🌙', lastUpdated: '1 week ago', content: DREAM_DOC },
      { id: 'ethics', title: 'Ethics Star', type: 'doc', icon: '⚖️', lastUpdated: '3 days ago', starred: true, content: ETHICS_DOC },
      { id: 'guardian', title: 'Guardian Star', type: 'doc', icon: '🛡️', lastUpdated: '3 days ago', content: GUARDIAN_DOC },
      { id: 'quantum', title: 'Quantum Star', type: 'doc', icon: '⚛️', lastUpdated: '2 days ago', content: QUANTUM_DOC },
    ]
  },
  {
    id: 'matriz',
    title: 'MATRIZ Engine',
    type: 'folder',
    icon: '🧠',
    children: [
      { id: 'matriz-pipeline', title: 'MATRIZ Pipeline', type: 'doc', icon: '📜🔍💭⚖️🎯⚡', lastUpdated: '2 days ago', starred: true, content: MATRIZ_DOC },
    ]
  },
  {
    id: 'policies',
    title: 'Policies',
    type: 'folder',
    icon: '📋',
    children: [
      { id: 'security', title: 'Security Policy', type: 'doc', icon: '🔒', lastUpdated: '2 weeks ago', content: SECURITY_DOC },
      { id: 'hr-handbook', title: 'HR Handbook', type: 'doc', icon: '📖', lastUpdated: '1 month ago', content: HR_DOC },
    ]
  },
]

export default function DocsLibrary() {
  const [search, setSearch] = useState('')
  const [expandedFolders, setExpandedFolders] = useState<string[]>(['getting-started'])
  const [selectedDoc, setSelectedDoc] = useState<DocItem | null>(null)

  const toggleFolder = (id: string) => {
    setExpandedFolders(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }

  const allDocs = DOCS_TREE.flatMap(folder =>
    folder.children?.map(doc => ({ ...doc, folder: folder.title })) || []
  )

  const filteredDocs = search
    ? allDocs.filter(doc =>
        doc.title.toLowerCase().includes(search.toLowerCase())
      )
    : null

  const recentDocs = allDocs.filter(d => d.lastUpdated?.includes('day')).slice(0, 3)
  const starredDocs = allDocs.filter(d => d.starred)

  return (
    <div className="flex h-[calc(100vh-120px)]">
      {/* Sidebar */}
      <aside className="w-72 border-r border-white/5 flex flex-col">
        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search docs..."
              className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:border-white/20"
            />
          </div>
        </div>

        {/* Search Results */}
        {filteredDocs && (
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-2">{filteredDocs.length} results</p>
            {filteredDocs.map(doc => (
              <button
                key={doc.id}
                onClick={() => { setSelectedDoc(doc); setSearch('') }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/5 transition-colors"
              >
                <span>{doc.icon}</span>
                <span className="text-sm text-white truncate">{doc.title}</span>
              </button>
            ))}
          </div>
        )}

        {/* Quick Access */}
        {!filteredDocs && (
          <>
            {starredDocs.length > 0 && (
              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Starred
                </p>
                {starredDocs.map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/5 transition-colors"
                  >
                    <span>{doc.icon}</span>
                    <span className="text-sm text-white truncate">{doc.title}</span>
                  </button>
                ))}
              </div>
            )}

            {recentDocs.length > 0 && (
              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Recent
                </p>
                {recentDocs.map(doc => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/5 transition-colors"
                  >
                    <span>{doc.icon}</span>
                    <span className="text-sm text-white truncate">{doc.title}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Navigation Tree */}
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {DOCS_TREE.map(folder => (
            <div key={folder.id} className="mb-1">
              <button
                onClick={() => toggleFolder(folder.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white/5 transition-colors"
              >
                {expandedFolders.includes(folder.id) ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <span>{folder.icon}</span>
                <span className="text-sm text-gray-300">{folder.title}</span>
              </button>

              {expandedFolders.includes(folder.id) && folder.children && (
                <div className="ml-4 pl-2 border-l border-white/5">
                  {folder.children.map(doc => (
                    <button
                      key={doc.id}
                      onClick={() => setSelectedDoc(doc)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors',
                        selectedDoc?.id === doc.id
                          ? 'bg-white/10 text-white'
                          : 'hover:bg-white/5 text-gray-400'
                      )}
                    >
                      <span className="text-sm">{doc.icon}</span>
                      <span className="text-sm truncate">{doc.title}</span>
                      {doc.starred && <Star className="w-3 h-3 text-lambda-gold ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        {selectedDoc ? (
          <DocViewer doc={selectedDoc} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Book className="w-12 h-12 text-gray-600 mb-4" />
            <h2 className="text-xl text-white mb-2">Internal Documentation</h2>
            <p className="text-gray-500 max-w-sm">
              Select a document from the sidebar to view it here
            </p>
          </div>
        )}
      </main>
    </div>
  )
}

function DocViewer({ doc }: { doc: DocItem }) {
  return (
    <div className="max-w-3xl mx-auto px-8 py-12">
      {/* Header */}
      <div className="mb-8 pb-8 border-b border-white/5">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-4xl">{doc.icon}</span>
          <h1 className="text-3xl font-light text-white">{doc.title}</h1>
        </div>
        {doc.lastUpdated && (
          <p className="text-sm text-gray-500">Last updated {doc.lastUpdated}</p>
        )}
      </div>

      {/* Content - Notion-style markdown rendering */}
      <article className="prose prose-invert prose-lg max-w-none">
        <MarkdownRenderer content={doc.content || ''} />
      </article>
    </div>
  )
}

function MarkdownRenderer({ content }: { content: string }) {
  const lines = content.split('\n')

  return (
    <div className="space-y-4">
      {lines.map((line, i) => {
        if (line.startsWith('# ')) {
          return <h1 key={i} className="text-2xl font-light text-white mt-8 mb-4">{line.slice(2)}</h1>
        }
        if (line.startsWith('## ')) {
          return <h2 key={i} className="text-xl font-light text-white mt-6 mb-3">{line.slice(3)}</h2>
        }
        if (line.startsWith('### ')) {
          return <h3 key={i} className="text-lg font-medium text-white mt-4 mb-2">{line.slice(4)}</h3>
        }
        if (line.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-2 text-gray-300">
              <span className="text-lambda-gold">•</span>
              <span>{line.slice(2)}</span>
            </div>
          )
        }
        if (line.startsWith('```')) {
          return null
        }
        if (line.startsWith('> ')) {
          return (
            <blockquote key={i} className="border-l-2 border-lambda-gold pl-4 text-gray-400 italic">
              {line.slice(2)}
            </blockquote>
          )
        }
        if (line.trim()) {
          return <p key={i} className="text-gray-300 leading-relaxed">{line}</p>
        }
        return null
      })}
    </div>
  )
}
